'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, CircleAlert, Copy, QrCode } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { useLiveAccount } from '@/lib/live-trading/useLiveTradingData';
import {
  BRIDGE_CHAINS,
  DEFAULT_CHAIN_ID,
  getTokenIconUrl,
  resolveBridgeAddress,
  type BridgeAddrType,
} from '@/lib/live-trading/bridge-assets';
import { ModalCloseButton, ModalOverlay } from './ModalShell';
import AddressQrCode from './AddressQrCode';

export interface DepositModalProps {
  open: boolean;
  onClose?: () => void;
}

const ADDR_TYPE_LABEL: Record<BridgeAddrType, string> = {
  evm: 'EVM address',
  svm: 'Solana address',
  btc: 'Bitcoin address',
  tron: 'Tron address',
};

function truncateAddress(address: string) {
  if (address.length <= 40) return address;
  return `${address.slice(0, 18)}...${address.slice(-18)}`;
}

function ChainBadge({ name, iconUrl }: { name: string; iconUrl?: string }) {
  if (iconUrl) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={iconUrl} alt={name} className="size-4.5 shrink-0 rounded-full object-cover" />;
  }
  return (
    <span className="flex size-4.5 shrink-0 items-center justify-center rounded-full bg-[#2a2a2a] text-[9px] font-semibold text-[#B7B9BC]">
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
}

interface SelectOption {
  value: string;
  label: string;
  /** 右侧附注（如最低充值额） */
  note?: string;
  /** 左侧图标（如链图标）；缺省首字母占位 */
  iconUrl?: string;
}

/** 下拉选择框（自绘，深色样式与弹窗一致），链 / 代币共用 */
function SelectBox({
  label,
  hint,
  value,
  options,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const current = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  return (
    <div ref={rootRef} className="relative flex min-w-0 flex-1 flex-col gap-2">
      <div className="flex items-center justify-between text-xs leading-4.5">
        <span className="text-white">{label}</span>
        {hint && <span className="text-[#B7B9BC]">{hint}</span>}
      </div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-11 w-full items-center justify-between rounded-xl border border-[#303036] bg-black px-2.5 transition-colors hover:border-[#3F3F46]"
      >
        <span className="flex min-w-0 items-center gap-1">
          <ChainBadge name={current?.label ?? '?'} iconUrl={current?.iconUrl} />
          <span className="truncate text-[13px] font-medium leading-5 text-white">
            {current?.label ?? value}
          </span>
        </span>
        <ChevronDown
          className={`size-4.5 shrink-0 text-[#B7B9BC] transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={1.5}
        />
      </button>
      {open && (
        <ul className="absolute top-full z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-[#303036] bg-[#111113] py-1 shadow-xl">
          {options.map((o) => (
            <li key={o.value}>
              <button
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2 px-2.5 py-2 text-left text-[13px] leading-5 transition-colors hover:bg-white/5 ${
                  o.value === value ? 'text-[#A772FF]' : 'text-white'
                }`}
              >
                <ChainBadge name={o.label} iconUrl={o.iconUrl} />
                <span className="flex-1 truncate">{o.label}</span>
                {o.note && <span className="shrink-0 text-xs text-[#757575]">{o.note}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * 充值弹窗。数据来自实盘账户的 Bridge 多链地址（useLiveAccount），
 * 链/代币/最低额参考表见 lib/live-trading/bridge-assets。
 * Figma: Pythra 产品版本管理 node 898-21315
 */
export default function DepositModal({ open, onClose }: DepositModalProps) {
  const { t } = useTranslation();
  const { account } = useLiveAccount();
  const [chainId, setChainId] = useState(DEFAULT_CHAIN_ID);
  const [token, setToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 仅 active 且桥地址就绪才解析；否则展示「地址生成中」占位
  const resolved =
    account?.status === 'active'
      ? resolveBridgeAddress(account.bridge_deposit_addresses, chainId)
      : null;
  const chain = resolved?.chain ?? BRIDGE_CHAINS.find((c) => c.id === chainId);
  const address = resolved?.address ?? '';
  // 当前 token 不在所选链的支持列表时回落到该链第一个（如切链后）
  const currentToken = token && chain?.tokens.includes(token) ? token : (chain?.tokens[0] ?? '');

  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    [],
  );

  const copyAddress = useCallback(async () => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // 剪贴板不可用时静默失败
    }
  }, [address]);

  return (
    <ModalOverlay open={open} onClose={onClose} className="w-full max-w-125">
      {/* 标题栏 */}
      <header className="flex h-15 items-center justify-between border-b border-black/10 px-5">
        <h2 className="text-base font-medium text-white/80">{t('Deposit to Your Wallet')}</h2>
        <ModalCloseButton onClose={onClose} />
      </header>
      <div className="p-5 space-y-5">
        {/* Bridge 标签 + 说明 */}
        <div className="flex flex-col gap-2">
          <span className="self-start rounded-full bg-[#142A22] px-1.5 py-1 text-xs leading-4 text-[#14C784]">
            {t('Bridge · Any chain')}
          </span>
          <p className="text-xs leading-4 text-[#757575]">
            {t('Deposit from any supported chain. Assets auto-convert to USDC.e on Polygon.')}
          </p>
        </div>

        {/* 链 / 币选择 + 最低充值提示 */}
        <div className="rounded-[10px] border border-[#2A261C]/50 bg-[#0C0B0A] p-4">
          <div className="flex gap-2.5">
            <SelectBox
              label={t('Select Chains')}
              // hint={chain ? `${t('Min')} $${chain.minUsd}` : undefined}
              value={chainId}
              options={BRIDGE_CHAINS.map((c) => ({
                value: c.id,
                label: c.label,
                note: `$${c.minUsd}+`,
                iconUrl: c.iconUrl,
              }))}
              onChange={setChainId}
            />
            <SelectBox
              label={t('Select Token')}
              value={currentToken}
              options={(chain?.tokens ?? []).map((name) => ({
                value: name,
                label: name,
                iconUrl: getTokenIconUrl(name),
              }))}
              onChange={setToken}
            />
          </div>
          <div className="mt-3 flex items-center gap-1.5">
            <CircleAlert className="size-4 shrink-0 text-[#E9AE18]" strokeWidth={1.5} />
            <p className="text-xs leading-4.5 text-[#E9AE18]">
              {t('Minimum deposit: ${{minUSD}}', { minUSD: `${chain?.minUsd}` })}
            </p>
          </div>
        </div>

        {/* 二维码 + 地址 */}
        <div className="rounded-[10px] border border-[#2A261C]/50 bg-[#0C0B0A] p-5">
          <div className="flex items-center gap-2 text-xs font-semibold leading-4 text-[#757575]">
            {t(chain ? ADDR_TYPE_LABEL[chain.addrType] : ADDR_TYPE_LABEL.evm)}
          </div>
          {address ? (
            <div className="flex items-center gap-2 mt-1">
              <p className="break-all text-sm leading-6 text-white">{address}</p>
              <button
                type="button"
                aria-label={t('Copy')}
                onClick={copyAddress}
                className="text-[#A772FF] transition-colors hover:text-[#B98AFF]"
              >
                {copied ? (
                  <Check className="size-3.75" strokeWidth={1.5} />
                ) : (
                  <Copy className="size-3.75" strokeWidth={1.25} />
                )}
              </button>
            </div>
          ) : (
            <p className="mt-1 text-sm leading-6 text-[#757575]">
              {t('Deposit address is being generated — available once the account is active.')}
            </p>
          )}
          <div className="my-5 h-px bg-[#1A1A1B]"></div>
          <div className="flex flex-col items-center gap-3">
            {address ? (
              <AddressQrCode value={address} iconUrl={chain?.iconUrl} />
            ) : (
              // 地址未就绪时显示占位
              <div className="flex size-43.5 items-center justify-center rounded-xl bg-[#1A1A1A]">
                <QrCode className="size-16 text-[#3F3F46]" strokeWidth={1} />
              </div>
            )}
            <p className="text-xs leading-4.5 text-[#757575]">
              {t('Scan or copy the address above to send your tokens.')}
            </p>
          </div>
        </div>
        <p className="text-xs leading-4 text-[#B7B9BC]">
          {t('Processing time: Usually < 5 minutes; Auto-converts to USDC.e on Polygon')}
        </p>
      </div>
    </ModalOverlay>
  );
}
